import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
  Collapse,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  ExpandMore,
  KeyboardArrowRight,
  Warning,
  Link as LinkIcon,
} from "@mui/icons-material";
import { TherapyType } from "../../../api-client";
import { useCatalogMethods, useCatalogTerms } from "../../../stores/MasterTables/therapies.queries";
import { useDeleteCatalogTerm } from "../../../stores/MasterTables/therapies.mutations";
import ModalTerm from "./ModalTerm";
import { ColorSwatch } from "./ColorField";

/**
 * "Begriffe" — the terms a doctor may write in the Heilmittelfeld.
 *
 * This is the level MD-232 introduced and the one with no previous admin
 * surface at all. A term on its own is meaningless; what makes it usable is
 *
 *   - its ALIASES: the spellings that resolve to it, and
 *   - its RESOLUTIONS: which methods may fulfil it, in priority order.
 *
 * Both are shown inline on the expanded row rather than behind another click,
 * because the question an admin actually has is "what happens when someone
 * writes this on a prescription", and that needs all three levels at once.
 */

interface Props {
  therapyType: TherapyType;
}

const TermsTab: React.FC<Props> = ({ therapyType }) => {
  const { data: terms, isLoading } = useCatalogTerms(therapyType);
  const { data: methods } = useCatalogMethods(therapyType);
  const deleteTerm = useDeleteCatalogTerm();

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const list: any[] = (terms as any[]) ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        t.label.toLowerCase().includes(q) ||
        (t.aliases ?? []).some((a: any) => a.alias.toLowerCase().includes(q)) ||
        (t.resolutions ?? []).some((r: any) => r.therapyMethodId.toLowerCase().includes(q))
    );
  }, [terms, search]);

  const handleDelete = (term: any) => {
    const blockers: string[] = [];
    if (term.usedByLines) blockers.push(`${term.usedByLines} Verordnungszeile(n)`);
    if (term.usedByDiagnosisGroups) blockers.push(`${term.usedByDiagnosisGroups} Diagnosegruppe(n)`);
    const warning = blockers.length
      ? `\n\nAchtung: wird verwendet von ${blockers.join(" und ")} — der Server wird das Löschen ablehnen.`
      : "";
    if (!window.confirm(`Begriff "${term.id}" löschen?${warning}`)) return;
    deleteTerm.mutate(term.id);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          label="Suchen (Begriff, Schreibweise, Methode)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 360 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} von {(terms as any[])?.length ?? 0}
        </Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setCreating(true)}>
          Begriff anlegen
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Ein Begriff ist nur nutzbar, wenn er mindestens <strong>eine Schreibweise</strong> hat
        (sonst findet ihn die Texterkennung nicht) und <strong>mindestens eine Methode</strong>
        {" "}auflöst (sonst gibt es nichts abzurechnen). Zeilen ohne beides sind markiert.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Begriff</TableCell>
              <TableCell>Bezeichnung</TableCell>
              <TableCell align="center" width={70}>
                Farbe
              </TableCell>
              <TableCell align="center">Schreibweisen</TableCell>
              <TableCell align="center">Methoden</TableCell>
              <TableCell align="center">Verwendung</TableCell>
              <TableCell align="right" width={110}>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((term) => {
              const noAlias = (term.aliases ?? []).length === 0;
              const noResolution = (term.resolutions ?? []).length === 0;
              const isOpen = expanded === term.id;
              return (
                <React.Fragment key={term.id}>
                  <TableRow
                    hover
                    sx={{
                      "& > *": { borderBottom: isOpen ? "unset" : undefined },
                      bgcolor: noAlias || noResolution ? "warning.50" : undefined,
                    }}
                  >
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(isOpen ? null : term.id)}>
                        {isOpen ? <ExpandMore /> : <KeyboardArrowRight />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                          {term.id}
                        </Typography>
                        {term.isSupplementary && (
                          <Chip label="Zuschlag" size="small" variant="outlined" color="secondary" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{term.label}</TableCell>
                    <TableCell align="center">
                      <ColorSwatch color={term.color} />
                    </TableCell>
                    <TableCell align="center">
                      {noAlias ? (
                        <Tooltip title="Ohne Schreibweise kann kein Verordnungstext auf diesen Begriff aufgelöst werden">
                          <Chip icon={<Warning />} label="keine" size="small" color="warning" />
                        </Tooltip>
                      ) : (
                        <Chip label={term.aliases.length} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {noResolution ? (
                        <Tooltip title="Ohne Methode gibt es nichts, was abgerechnet werden könnte">
                          <Chip icon={<Warning />} label="keine" size="small" color="warning" />
                        </Tooltip>
                      ) : (
                        <Chip label={term.resolutions.length} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Verordnungszeilen / Diagnosegruppen">
                        <span>
                          <Chip
                            label={`${term.usedByLines} / ${term.usedByDiagnosisGroups}`}
                            size="small"
                            variant="outlined"
                            color={term.usedByLines ? "primary" : "default"}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setEditing(term)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <Tooltip
                        title={
                          term.usedByLines || term.usedByDiagnosisGroups
                            ? "Wird verwendet — Löschen wird abgelehnt"
                            : "Löschen"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(term)}
                            disabled={deleteTerm.isPending}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={8}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 4 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Schreibweisen
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                            {(term.aliases ?? []).length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                keine
                              </Typography>
                            )}
                            {(term.aliases ?? []).map((a: any) => (
                              <Tooltip key={a.alias} title={`normalisiert: ${a.normalizedAlias}`}>
                                <Chip label={a.alias} size="small" />
                              </Tooltip>
                            ))}
                          </Stack>

                          <Typography variant="subtitle2" gutterBottom>
                            Auflösung — beste zuerst
                          </Typography>
                          {(term.resolutions ?? []).length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              keine
                            </Typography>
                          )}
                          <Stack spacing={1}>
                            {(term.resolutions ?? []).map((r: any) => (
                              <Stack
                                key={r.therapyMethodId}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  label={`Prio ${r.priority}`}
                                  size="small"
                                  color={r.priority >= 100 ? "primary" : "default"}
                                />
                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                  {r.therapyMethodId}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {r.methodLabel}
                                </Typography>
                                <KeyboardArrowRight fontSize="small" color="disabled" />
                                {r.lineItems.length === 0 ? (
                                  <Tooltip title="Diese Methode hat keine abrechenbare Zeile — die Auflösung läuft ins Leere">
                                    <Chip
                                      icon={<Warning />}
                                      label="keine Abrechnung"
                                      size="small"
                                      color="warning"
                                    />
                                  </Tooltip>
                                ) : (
                                  r.lineItems.map((i: any) => (
                                    <Tooltip
                                      key={i.id}
                                      title={`${i.tarifGroupId ?? "?"} · ${i.therapyType ?? "alle"} · Position ${
                                        i.lineItem ?? "—"
                                      }`}
                                    >
                                      <Chip
                                        icon={<LinkIcon />}
                                        label={i.id}
                                        size="small"
                                        variant={i.inUse ? "filled" : "outlined"}
                                        color={i.inUse ? "success" : "default"}
                                      />
                                    </Tooltip>
                                  ))
                                )}
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(creating || editing) && (
        <ModalTerm
          open
          term={editing}
          methods={(methods as any[]) ?? []}
          terms={(terms as any[]) ?? []}
          therapyType={therapyType}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Box>
  );
};

export default TermsTab;
